
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link as LinkIcon, FileText, Eye, Users, Copy, Sparkles, Lock as LockIcon, UploadCloud, LogOut, AlertCircle, ExternalLink, ChevronRight, MessageCircle, AlertTriangle, Folder, Timer, DollarSign, Briefcase, ChevronDown, ChevronUp, Smartphone, Image as ImageIcon, User, Settings, Shield, Download, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { lockService } from '../services/lockService';
import { adminService } from '../services/adminService';
import { Lock, LockType, CreateLockDTO, LockConfig, Lead } from '../types';
import { Button } from './ui/Button';
import { LockPreview } from './LockPreview';

const Dashboard: React.FC = () => {
  const [user, setUser] = useState<any>(null);
  const [locks, setLocks] = useState<Lock[]>([]);
  const [allLeads, setAllLeads] = useState<Lead[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const navigate = useNavigate();
  const profileMenuRef = useRef<HTMLDivElement>(null);
  
  // Creation State
  const [targetUrl, setTargetUrl] = useState('');
  const [lockType, setLockType] = useState<LockType>(LockType.LINK);
  const [title, setTitle] = useState('');
  const [customImage, setCustomImage] = useState('');
  const [enableSocial, setEnableSocial] = useState(false);
  const [socialUrl, setSocialUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(true);

  // New Power-Ups State
  const [enableTips, setEnableTips] = useState(false);
  const [tipAmount, setTipAmount] = useState('3.00');
  const [enableFomo, setEnableFomo] = useState(false);
  const [fomoType, setFomoType] = useState<'timer'|'limit'>('timer');
  const [fomoText, setFomoText] = useState('');
  const [enableB2B, setEnableB2B] = useState(false);
  const [enableBlur, setEnableBlur] = useState(true);

  // File Upload State
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  // Environment State
  const [isLocalhost, setIsLocalhost] = useState(false);
  const [origin, setOrigin] = useState('');

  useEffect(() => {
    // Determine the canonical origin for display
    const host = window.location.hostname;
    const isDev = host.includes('localhost') || host.includes('127.0.0.1') || host.includes('googleusercontent');
    
    setIsLocalhost(isDev);
    setOrigin(isDev ? window.location.origin : 'https://lockd.in');

    supabase.auth.getUser().then(({ data: { user } }) => {
        if (user) {
            setUser(user);
            loadData(user.id);
        }
    });

    const handleClickOutside = (event: MouseEvent) => {
        if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
            setShowProfileMenu(false);
        }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Set default FOMO text when type changes if empty
  useEffect(() => {
      if (!fomoText && enableFomo) {
          if (fomoType === 'timer') setFomoText('Offer expires in 24h');
          else setFomoText('Only 5 spots left');
      }
  }, [fomoType, enableFomo]);

  const previewConfig: LockConfig = useMemo(() => ({
    tip: enableTips ? {
        enabled: true,
        amount: parseFloat(tipAmount) || 3.00
    } : undefined,
    fomo: enableFomo ? {
        enabled: true,
        type: fomoType,
        text: fomoText || (fomoType === 'timer' ? 'Expires soon' : 'Limited availability')
    } : undefined,
    security: enableB2B ? {
        b2bOnly: true
    } : undefined,
    preview: {
        blurStrength: enableBlur ? 'high' : 'none'
    },
    files: {
        count: selectedFiles.length,
        isFolder: selectedFiles.length > 1
    }
  }), [enableTips, tipAmount, enableFomo, fomoType, fomoText, enableB2B, enableBlur, selectedFiles]);

  const loadData = async (userId: string) => {
      try {
          const locksData = await lockService.getUserLocks(userId);
          setLocks(locksData);
          
          // Fetch global leads across all locks
          const leadsData = await lockService.getAllUserLeads(userId);
          setAllLeads(leadsData);
      } catch (e) {
          console.error("Failed to load dashboard data", e);
      }
  };

  const handleGlobalExportCSV = () => {
    if (!allLeads.length) return;
    
    const headers = ['Email', 'Date', 'Device Type', 'Lock Source'];
    const rows = allLeads.map(l => [
        l.email,
        new Date(l.createdAt).toLocaleString(),
        l.deviceType,
        l.lockTitle || 'Unknown'
    ]);

    const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `all-leads-export-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleLogout = async () => {
      await supabase.auth.signOut();
      navigate('/');
  };

  const handleCreateStart = () => {
    setIsCreating(true);
  };

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        setSelectedFiles(Array.from(e.dataTransfer.files));
        setLockType(LockType.FILE);
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
          setSelectedFiles(Array.from(e.target.files));
      }
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting || !user) return;
    setIsSubmitting(true);

    try {
        let finalTargetUrl = targetUrl;
        let finalTitle = title;

        if (lockType === LockType.FILE && selectedFiles.length > 0) {
            if (!finalTitle) {
                finalTitle = selectedFiles.length > 1 
                    ? `${selectedFiles.length} Files (${selectedFiles[0].name}...)`
                    : selectedFiles[0].name;
            }
            finalTargetUrl = ""; 
        } else if (lockType === LockType.LINK && !finalTitle) {
            finalTitle = "Exclusive Link Content";
        }

        const payload: CreateLockDTO = {
            type: lockType,
            targetUrl: finalTargetUrl,
            title: finalTitle,
            imageUrl: customImage,
            buttonText: enableSocial ? 'Unlock & Comment' : 'Unlock Access 🔓',
            socialUrl: enableSocial ? socialUrl : undefined,
            socialType: enableSocial ? 'profile' : undefined,
            config: previewConfig
        };

        await lockService.createLock(user.id, payload, selectedFiles);

        setIsCreating(false);
        setTargetUrl('');
        setTitle('');
        setCustomImage('');
        setSelectedFiles([]);
        setLockType(LockType.LINK);
        setEnableSocial(false);
        setSocialUrl('');
        setEnableTips(false);
        setEnableFomo(false);
        setFomoText('');
        setEnableB2B(false);
        loadData(user.id);

    } catch (error: any) {
        console.error(error);
        alert('Error creating lock: ' + error.message);
    } finally {
        setIsSubmitting(false);
    }
  };

  const copyToClipboard = (e: React.MouseEvent, slug: string) => {
    e.stopPropagation();
    const url = `${origin}/#/l/${slug}`;
    navigator.clipboard.writeText(url);
    alert("Link copied! You can now share it.");
  };

  const getPreviewImage = (l: Lock) => l.imageUrl || `https://picsum.photos/seed/${l.id}/800/600`;

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 pb-20">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center cursor-pointer group" onClick={() => navigate('/')}>
            <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center shadow-lg shadow-slate-900/20 group-hover:scale-105 transition-transform">
              <LockIcon className="w-5 h-5 text-white fill-white/20" />
            </div>
          </div>
          <div className="flex items-center space-x-3" ref={profileMenuRef}>
             {adminService.isAdmin(user?.email) && (
                 <Button variant="ghost" onClick={() => navigate('/admin')} className="h-9 px-3 text-xs text-red-600 hover:bg-red-50">
                    <Shield className="w-3.5 h-3.5 mr-1.5" /> Admin
                 </Button>
             )}

             <div className="relative">
                 <button 
                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                    className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 pl-2 pr-3 py-1.5 rounded-full transition-colors"
                 >
                     <div className="w-7 h-7 bg-slate-300 rounded-full flex items-center justify-center overflow-hidden">
                         {user?.user_metadata?.avatar_url ? (
                             <img src={user.user_metadata.avatar_url} alt="User" className="w-full h-full object-cover" />
                         ) : (
                             <User className="w-4 h-4 text-slate-600" />
                         )}
                     </div>
                     <ChevronDown className={`w-3 h-3 text-slate-500 transition-transform ${showProfileMenu ? 'rotate-180' : ''}`} />
                 </button>
                 
                 <AnimatePresence>
                     {showProfileMenu && (
                         <motion.div 
                            initial={{ opacity: 0, y: 5, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 5, scale: 0.95 }}
                            className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden z-50"
                         >
                             <div className="px-4 py-3 border-b border-slate-100">
                                 <div className="text-xs font-medium text-slate-900 truncate">{user?.email}</div>
                                 <div className="text-[10px] text-slate-500">Beta Free Plan</div>
                             </div>
                             <button onClick={() => navigate('/profile')} className="w-full text-left px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 flex items-center gap-2">
                                 <Settings className="w-4 h-4" /> Account
                             </button>
                             <button onClick={handleLogout} className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                                 <LogOut className="w-4 h-4" /> Sign Out
                             </button>
                         </motion.div>
                     )}
                 </AnimatePresence>
             </div>
          </div>
        </div>
      </header>

      <main className={`mx-auto px-4 py-8 sm:px-6 transition-all duration-500 ${isCreating ? 'max-w-6xl' : 'max-w-3xl'}`}>
        
        {isLocalhost && (
            <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-8 flex items-start gap-3 max-w-3xl mx-auto"
            >
                <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                    <h3 className="text-sm font-semibold text-amber-800">Development Mode</h3>
                    <p className="text-sm text-amber-700 mt-1">
                        Links generated here will use {origin}. Deploy to Production to use lockd.in.
                    </p>
                </div>
            </motion.div>
        )}

        {!isCreating && (
            <div className="text-center mb-12">
                <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-4 text-slate-900">
                    Gate your content.<br/>
                    <span className="text-slate-400">Capture leads instantly.</span>
                </h1>
            </div>
        )}

        {!isCreating ? (
             <motion.div 
               layoutId="create-card"
               className="bg-white p-2 rounded-full shadow-xl shadow-slate-200 border border-slate-100 max-w-xl mx-auto flex items-center cursor-pointer hover:ring-4 hover:ring-slate-100 transition-all"
               onClick={handleCreateStart}
             >
               <div className="pl-6 pr-4 text-slate-400">
                 <LinkIcon className="w-5 h-5" />
               </div>
               <div className="flex-1 text-left text-slate-400 text-base">
                  Paste a link or drop files...
               </div>
               <Button className="h-10 px-6 text-sm pointer-events-none">
                 Create Lock
               </Button>
             </motion.div>
        ) : (
            <div className="flex flex-col lg:flex-row items-start gap-8 lg:gap-12">
              {/* LEFT COLUMN: FORM */}
              <motion.div 
                  layoutId="create-card"
                  className="flex-1 w-full bg-white rounded-3xl shadow-2xl shadow-slate-200 border border-slate-100 overflow-hidden text-left order-1 lg:order-1"
              >
                  <form onSubmit={handleCreateSubmit} className="p-6 sm:p-8 space-y-6">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="text-lg font-semibold">New Lock Configuration</h3>
                      <button type="button" onClick={() => setIsCreating(false)} className="text-slate-400 hover:text-slate-600 text-sm">Cancel</button>
                    </div>

                    {/* Type Toggle */}
                    <div className="flex bg-slate-100 p-1 rounded-xl">
                      <button
                        type="button"
                        onClick={() => setLockType(LockType.LINK)}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${lockType === LockType.LINK ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`}
                      >
                        <LinkIcon className="w-4 h-4 inline mr-2" /> Link
                      </button>
                      <button
                        type="button"
                        onClick={() => setLockType(LockType.FILE)}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${lockType === LockType.FILE ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`}
                      >
                        <FileText className="w-4 h-4 inline mr-2" /> File(s)
                      </button>
                    </div>

                    {/* Input Fields */}
                    <div>
                      {lockType === LockType.LINK ? (
                          <>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Target URL</label>
                            <input 
                                required
                                autoFocus
                                value={targetUrl}
                                onChange={(e) => setTargetUrl(e.target.value)}
                                placeholder="https://..."
                                className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-slate-900 focus:ring-slate-900 focus:outline-none transition-colors"
                            />
                          </>
                      ) : (
                          <div 
                            onDragOver={onDragOver}
                            onDragLeave={onDragLeave}
                            onDrop={onDrop}
                            className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-colors ${isDragging ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:border-slate-400'}`}
                          >
                              <input 
                                type="file"
                                multiple 
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                onChange={handleFileSelect}
                              />
                              <div className="flex flex-col items-center justify-center pointer-events-none">
                                {selectedFiles.length > 0 ? (
                                    <>
                                        {selectedFiles.length > 1 ? (
                                            <Folder className="w-10 h-10 text-slate-900 mb-3" />
                                        ) : (
                                            <FileText className="w-10 h-10 text-slate-900 mb-3" />
                                        )}
                                        <p className="text-sm font-semibold text-slate-900">
                                            {selectedFiles.length > 1 ? `${selectedFiles.length} files selected` : selectedFiles[0].name}
                                        </p>
                                        <p className="text-xs text-slate-500">
                                            {(selectedFiles.reduce((acc, f) => acc + f.size, 0) / 1024 / 1024).toFixed(2)} MB Total
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <UploadCloud className="w-10 h-10 text-slate-400 mb-3" />
                                        <p className="text-sm font-medium text-slate-700">Click to upload files</p>
                                        <p className="text-xs text-slate-400 mt-1">Supports multiple files</p>
                                    </>
                                )}
                              </div>
                          </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                      <input 
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                          placeholder="e.g. 2024 Strategy Deck"
                          className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-slate-900 focus:ring-slate-900 focus:outline-none transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Cover Image URL (Optional)</label>
                      <div className="flex gap-2">
                          <div className="relative flex-1">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <ImageIcon className="h-4 w-4 text-slate-400" />
                            </div>
                            <input 
                                value={customImage}
                                onChange={(e) => setCustomImage(e.target.value)}
                                placeholder="https://..."
                                className="w-full pl-9 pr-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-slate-900 focus:ring-slate-900 focus:outline-none transition-colors"
                            />
                          </div>
                          {customImage && (
                              <div className="w-12 h-12 rounded-lg bg-slate-100 overflow-hidden border border-slate-200 shrink-0">
                                  <img src={customImage} alt="Preview" className="w-full h-full object-cover" onError={(e) => (e.currentTarget.style.display = 'none')} />
                              </div>
                          )}
                      </div>
                    </div>

                    {/* Social Boost Toggle */}
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${enableSocial ? 'bg-blue-100 text-blue-600' : 'bg-slate-200 text-slate-500'}`}>
                                    <MessageCircle className="w-4 h-4" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-900">Boost Engagement</label>
                                    <p className="text-xs text-slate-500">Force comment to unlock</p>
                                </div>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input type="checkbox" className="sr-only peer" checked={enableSocial} onChange={(e) => setEnableSocial(e.target.checked)} />
                                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                        </div>
                        <AnimatePresence>
                            {enableSocial && (
                                <motion.div 
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="overflow-hidden pt-2"
                                >
                                    <input 
                                        value={socialUrl}
                                        onChange={(e) => setSocialUrl(e.target.value)}
                                        placeholder="Your Profile URL (Fallback)"
                                        className="w-full px-3 py-2 text-sm rounded-lg bg-white border border-slate-200 focus:border-blue-500 outline-none"
                                    />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Power Ups */}
                    <div className="border-t border-slate-100 pt-4">
                        <button 
                            type="button"
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            className="flex items-center justify-between w-full text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors mb-4"
                        >
                            <span>Power-Ups (Customize Experience)</span>
                            {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        
                        <AnimatePresence>
                            {showAdvanced && (
                                <motion.div 
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="space-y-4 overflow-hidden"
                                >
                                    {/* Tip Jar (DISABLED - COMING SOON) */}
                                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100 opacity-60 relative overflow-hidden">
                                        <div className="absolute right-0 top-0 bg-slate-900 text-white text-[9px] px-2 py-0.5 rounded-bl-lg font-bold z-10">
                                            COMING SOON
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-slate-200 text-slate-400">
                                                <DollarSign className="w-4 h-4" />
                                            </div>
                                            <div>
                                                <div className="text-sm font-medium">Tip Jar</div>
                                                <div className="text-xs text-slate-400">Ask for support</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3 pointer-events-none">
                                            <input type="checkbox" disabled className="w-4 h-4 accent-green-600" />
                                        </div>
                                    </div>

                                    {/* FOMO */}
                                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${enableFomo ? 'bg-red-100 text-red-600' : 'bg-slate-200 text-slate-400'}`}>
                                                    <Timer className="w-4 h-4" />
                                                </div>
                                                <div>
                                                    <div className="text-sm font-medium">Urgency (FOMO)</div>
                                                    <div className="text-xs text-slate-400">Scarcity countdown</div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                {enableFomo && (
                                                    <select 
                                                        value={fomoType} 
                                                        onChange={(e) => setFomoType(e.target.value as any)}
                                                        className="text-xs border border-slate-200 rounded-md py-1 bg-white"
                                                    >
                                                        <option value="timer">Timer Mode</option>
                                                        <option value="limit">Limit Mode</option>
                                                    </select>
                                                )}
                                                <input type="checkbox" checked={enableFomo} onChange={(e) => setEnableFomo(e.target.checked)} className="w-4 h-4 accent-red-600" />
                                            </div>
                                        </div>
                                        
                                        <AnimatePresence>
                                            {enableFomo && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    className="overflow-hidden pl-11"
                                                >
                                                    <label className="block text-xs font-medium text-slate-500 mb-1">Custom text label</label>
                                                    <input 
                                                        value={fomoText}
                                                        onChange={(e) => setFomoText(e.target.value)}
                                                        placeholder={fomoType === 'timer' ? "Expires in 24h" : "85/100 claimed"}
                                                        className="w-full px-3 py-2 text-sm rounded-lg bg-white border border-slate-200 focus:border-red-500 outline-none placeholder-slate-300"
                                                    />
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>

                                    {/* B2B */}
                                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${enableB2B ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-200 text-slate-400'}`}>
                                                <Briefcase className="w-4 h-4" />
                                            </div>
                                            <div>
                                                <div className="text-sm font-medium">B2B Gating</div>
                                                <div className="text-xs text-slate-400">Reject Gmail/Yahoo</div>
                                            </div>
                                        </div>
                                        <input type="checkbox" checked={enableB2B} onChange={(e) => setEnableB2B(e.target.checked)} className="w-4 h-4 accent-indigo-600" />
                                    </div>

                                    {/* Preview Blur (DISABLED - COMING SOON) */}
                                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100 opacity-60 relative overflow-hidden">
                                        <div className="absolute right-0 top-0 bg-slate-900 text-white text-[9px] px-2 py-0.5 rounded-bl-lg font-bold z-10">
                                            COMING SOON
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-slate-200 text-slate-400">
                                                <Eye className="w-4 h-4" />
                                            </div>
                                            <div>
                                                <div className="text-sm font-medium">Magic Preview</div>
                                                <div className="text-xs text-slate-400">Blur background teaser</div>
                                            </div>
                                        </div>
                                        <input type="checkbox" disabled className="w-4 h-4 accent-purple-600 pointer-events-none" />
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    <Button type="submit" fullWidth isLoading={isSubmitting} className="h-12 text-base">
                      {isSubmitting ? 'Publishing...' : 'Publish Lock'} <Sparkles className="ml-2 w-4 h-4" />
                    </Button>
                  </form>
              </motion.div>

              {/* RIGHT COLUMN: PREVIEW */}
              <div className="w-full lg:w-[350px] order-last lg:order-2 relative lg:sticky lg:top-24 flex flex-col items-center z-10">
                 <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-6 flex items-center gap-2 mt-8 lg:mt-0">
                    <Smartphone className="w-4 h-4" /> Live Mobile Preview
                 </h3>
                 <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="w-full"
                 >
                    <LockPreview 
                        title={title || "Exclusive Content"}
                        type={lockType}
                        imageUrl={customImage}
                        buttonText={enableSocial ? 'Unlock & Comment' : 'Unlock Access 🔓'}
                        config={previewConfig}
                        socialEnabled={enableSocial}
                    />
                 </motion.div>
              </div>
            </div>
        )}

        {!isCreating && (
            <>
                {/* KPI Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                    <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100">
                        <div className="text-slate-500 text-sm font-medium mb-1">Total Views</div>
                        <div className="text-3xl font-bold text-slate-900">
                            {locks.reduce((acc, curr) => acc + (curr.viewsCount || 0), 0)}
                        </div>
                    </div>
                    <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 relative overflow-hidden group">
                        <div className="relative z-10">
                            <div className="flex justify-between items-start">
                                <div>
                                    <div className="text-slate-500 text-sm font-medium mb-1">Leads Captured</div>
                                    <div className="text-3xl font-bold text-slate-900">
                                        {allLeads.length}
                                    </div>
                                </div>
                                <Button 
                                    onClick={handleGlobalExportCSV} 
                                    disabled={allLeads.length === 0} 
                                    className="h-9 px-3 text-xs bg-slate-900 hover:bg-black text-white"
                                >
                                    <Download className="w-3.5 h-3.5 mr-1.5" /> Export All CSV
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Active Locks List */}
                <h2 className="text-xl font-bold text-slate-900 mb-4 px-2 mt-8">Your Active Locks</h2>
                <div className="space-y-4 mb-12">
                <AnimatePresence>
                    {locks.length === 0 && (
                    <div className="text-center py-12 text-slate-400 bg-white rounded-3xl border border-slate-100 border-dashed">
                        <p>No locks yet. Create your first one above.</p>
                    </div>
                    )}
                    {locks.map((lock) => (
                    <motion.div 
                        key={lock.id}
                        layout
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        onClick={() => navigate(`/lock/${lock.id}`)}
                        className="group bg-white p-4 sm:p-5 rounded-[24px] shadow-sm border border-slate-100 hover:shadow-md transition-all flex flex-col sm:flex-row sm:items-center gap-4 cursor-pointer relative"
                    >
                        {lock.socialType === 'profile' && (
                            <div className="absolute top-0 right-0 transform translate-x-2 -translate-y-2 bg-orange-500 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-lg flex items-center gap-1 z-10 animate-pulse">
                                <AlertTriangle className="w-3 h-3" /> Action Required
                            </div>
                        )}

                        <div className="w-full sm:w-16 h-32 sm:h-16 rounded-2xl bg-slate-200 overflow-hidden flex-shrink-0 relative">
                        <img src={getPreviewImage(lock)} alt={lock.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                        <div className="absolute inset-0 bg-black/10" />
                        <div className="absolute bottom-0 right-0 p-1 flex gap-0.5">
                                {lock.config?.tip?.enabled && (
                                    <div className="bg-green-100 text-green-600 p-1 rounded-full shadow-sm">
                                        <DollarSign className="w-2.5 h-2.5" />
                                    </div>
                                )}
                                {lock.config?.security?.b2bOnly && (
                                    <div className="bg-indigo-100 text-indigo-600 p-1 rounded-full shadow-sm">
                                        <Briefcase className="w-2.5 h-2.5" />
                                    </div>
                                )}
                        </div>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-bold text-slate-900 truncate">{lock.title}</h3>
                            <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full shrink-0 ${lock.type === LockType.FILE ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'}`}>
                                {lock.type}
                            </span>
                        </div>
                        <div className="flex items-center text-xs text-slate-500">
                                <ExternalLink className="w-3 h-3 mr-1 shrink-0" />
                                <span className="truncate">{origin}/#/l/{lock.slug}</span>
                        </div>
                        </div>

                        <div className="flex items-center justify-between sm:justify-end gap-6 w-full sm:w-auto border-t sm:border-t-0 border-slate-100 pt-4 sm:pt-0">
                        <div className="flex gap-4">
                            <div className="text-center flex items-center text-slate-500 text-xs font-medium">
                                <Eye className="w-3.5 h-3.5 mr-1.5"/> {lock.viewsCount || 0}
                            </div>
                            <div className="text-center flex items-center text-slate-500 text-xs font-medium">
                                <Users className="w-3.5 h-3.5 mr-1.5"/> {lock.leadsCount || 0}
                            </div>
                        </div>
                        
                        <div className="flex gap-1">
                            <Button variant="ghost" onClick={(e) => copyToClipboard(e, lock.slug)} title="Copy Link" className="h-9 w-9 rounded-full px-0 hover:bg-slate-100">
                                <Copy className="w-4 h-4" />
                            </Button>
                            <div className="w-px h-6 bg-slate-100 mx-1 my-auto"></div>
                            <div className="text-slate-300">
                                <ChevronRight className="w-5 h-5" />
                            </div>
                        </div>
                        </div>
                    </motion.div>
                    ))}
                </AnimatePresence>
                </div>

                {/* RECENT LEADS TABLE (GLOBAL) */}
                <h2 className="text-xl font-bold text-slate-900 mb-4 px-2">Latest Leads (All Locks)</h2>
                <div className="bg-white rounded-[24px] border border-slate-200 shadow-sm overflow-hidden mb-12">
                    {allLeads.length === 0 ? (
                         <div className="text-center py-12 text-slate-400">
                             <Users className="w-8 h-8 mx-auto mb-2 opacity-20" />
                             No leads captured yet across any locks.
                         </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead>
                                    <tr className="bg-slate-50/50 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                        <th className="p-4">Email</th>
                                        <th className="p-4">Source Lock</th>
                                        <th className="p-4">Date</th>
                                        <th className="p-4 text-right">Device</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50 text-sm">
                                    {allLeads.slice(0, 10).map((lead) => (
                                        <tr key={lead.id} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="p-4 font-medium text-slate-900">{lead.email}</td>
                                            <td className="p-4 text-slate-500 truncate max-w-[150px]">{lead.lockTitle || 'Unknown'}</td>
                                            <td className="p-4 text-slate-500 flex items-center gap-2">
                                                <Calendar className="w-3.5 h-3.5 text-slate-300" />
                                                {new Date(lead.createdAt).toLocaleDateString()}
                                            </td>
                                            <td className="p-4 text-right">
                                                <span className={`inline-flex items-center px-2 py-1 rounded text-[10px] font-bold uppercase ${lead.deviceType === 'mobile' ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>
                                                    {lead.deviceType}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {allLeads.length > 10 && (
                                <div className="p-3 text-center border-t border-slate-100 bg-slate-50/50 text-xs text-slate-400">
                                    Showing recent 10 of {allLeads.length} leads
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
